import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { sanitizeBoardHtml } from '../../utils/boardModeration';
import { linkifyHtmlText } from '../../utils/linkify';
import { useOnlineStatus } from '../../utils/onlineStatus';

export default function BoardRichContent({ html = '', className = 'board-rich-content' }) {
  const ref = useRef(null);
  const online = useOnlineStatus();
  const safeHtml = useMemo(() => linkifyHtmlText(sanitizeBoardHtml(html || '')), [html]);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const cleanups = [];
    root.querySelectorAll('figure[data-giphy-embed="true"]').forEach((figure) => {
      const image = figure.querySelector('img[data-giphy-image]');
      const title = figure.getAttribute('data-giphy-title') || image?.getAttribute('alt') || 'GIF';
      figure.setAttribute('data-giphy-title', title);

      const markOffline = () => {
        figure.classList.add('is-giphy-offline');
        if (image?.getAttribute('src')) {
          image.setAttribute('data-member-nest-src', image.getAttribute('src'));
          image.removeAttribute('src');
        }
      };
      const markOnline = () => {
        figure.classList.remove('is-giphy-offline');
        const savedSrc = image?.getAttribute('data-member-nest-src');
        if (image && savedSrc && !image.getAttribute('src')) {
          image.setAttribute('src', savedSrc);
        }
      };

      if (online) markOnline();
      else markOffline();

      if (image) {
        const handleError = () => markOffline();
        image.addEventListener('error', handleError);
        cleanups.push(() => image.removeEventListener('error', handleError));
      }
    });

    return () => cleanups.forEach((cleanup) => cleanup());
  }, [online, safeHtml]);

  return (
    <div
      ref={ref}
      className={className}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}

BoardRichContent.propTypes = {
  html: PropTypes.string,
  className: PropTypes.string,
};
